/**
 * Registration Page
 *
 * New user registration with password strength validation.
 */

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import PasswordStrengthMeter from '../../components/auth/PasswordStrengthMeter';

export function RegisterPage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);

  // Get invite token from URL if present
  const inviteToken = searchParams.get('invite');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setLocalError(t('auth.passwordsDoNotMatch'));
      return;
    }

    // Validate password length
    if (password.length < 12) {
      setLocalError(t('auth.passwordMinLength'));
      return;
    }

    // Validate display name
    if (!displayName || displayName.trim().length === 0) {
      setLocalError(t('auth.displayNameRequired'));
      return;
    }

    if (displayName.trim().length < 2 || displayName.trim().length > 100) {
      setLocalError(t('auth.displayNameLength'));
      return;
    }

    const result = await register({
      email,
      password,
      displayName: displayName.trim(),
      inviteToken: inviteToken || undefined,
      locale: i18n.language, // Pass current language for email localization
    });

    if (result.success) {
      // If requires approval or email verification, show message instead of navigating
      if (result.requiresApproval || result.requiresEmailVerification) {
        setSuccessMessage(result.message || 'Registration successful!');
        setRequiresApproval(result.requiresApproval || false);
        // Don't navigate, keep user on page to see the message
      } else {
        // Normal flow - navigate to app
        navigate('/formspace');
      }
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.appName')}</h1>
          <h2 className="mt-6 text-2xl font-semibold text-gray-900">
            {t('auth.createAccountTitle')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>

        {/* Invite notice */}
        {inviteToken && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
            {t('auth.inviteNotice')}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-green-400 mt-0.5 ltr:mr-3 rtl:ml-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium">{successMessage}</p>
                {requiresApproval && (
                  <p className="mt-2 text-sm">
                    You can close this page. We'll send you an email when your account is approved.
                  </p>
                )}
                <div className="mt-3">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-green-700 hover:text-green-600"
                  >
                    Go to login page →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {!successMessage && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {/* Error message */}
            {displayError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {displayError}
              </div>
            )}

            <div className="space-y-4">
            {/* Display name (required) */}
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                {t('auth.displayName')}
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                autoComplete="name"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.displayNamePlaceholder')}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth.emailAddress')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth.createPasswordPlaceholder')}
              />
              <PasswordStrengthMeter password={password} />
            </div>

            {/* Confirm password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('auth.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                }`}
                placeholder={t('auth.confirmPasswordPlaceholder')}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{t('auth.passwordsDoNotMatch')}</p>
              )}
            </div>
          </div>

          {/* Terms */}
          <p className="text-xs text-gray-500">
            {t('auth.termsAgreement')}{' '}
            <Link to="/terms" className="text-blue-600 hover:underline">
              {t('auth.termsOfService')}
            </Link>{' '}
            {t('auth.and')}{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">
              {t('auth.privacyPolicy')}
            </Link>
            .
          </p>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading || (password !== confirmPassword && confirmPassword.length > 0)}
            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('auth.creatingAccount')}
              </span>
            ) : (
              t('auth.createAccount')
            )}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
